import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Download, TrendingUp, TrendingDown, Minus, PackageSearch, Search, X, Sparkles, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import {
  deriveSeries, baseModel, coreModel, condenserTons, modelFamily, SERIES_LABEL, SYSTEM_CATEGORIES, AUX_CATEGORIES, CATEGORY_LABEL,
  type EquipCategory, type EquipSeries,
} from "@/lib/equipment";
import { useEquipmentInstalls, useStockOnHand, type EquipInstallRow } from "./api";

// ============================================================================
// REPORT-04 (phase a) — Seasonal demand & projection. The front half of Seth's
// "order to end the season at ~0 inventory" tool: how many of each model we
// installed in a given season across recent years, the trend, and a naive
// projection for next season. The ORDER TARGET (= projected demand − current
// stock on hand) waits on the stock source (INSTOCK UNITS.xlsx) being wired —
// shown explicitly as pending so the report never fakes a number it can't back.
//
// All demand data is transactional install history (mirror_equipment) — the
// trustworthy half. (ESC's own stock-on-hand is unreliable per Seth; that's why
// the stock side comes from the Sales spreadsheet, cross-checked, later.)
// ============================================================================

type SeasonKey = "summer" | "winter" | "spring" | "fall" | "year";
const SEASONS: { key: SeasonKey; label: string; months: number[] }[] = [
  { key: "summer", label: "Summer (Jun–Aug)", months: [6, 7, 8] },
  { key: "winter", label: "Winter (Dec–Feb)", months: [12, 1, 2] },
  { key: "spring", label: "Spring (Mar–May)", months: [3, 4, 5] },
  { key: "fall", label: "Fall (Sep–Nov)", months: [9, 10, 11] },
  { key: "year", label: "Full Year", months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] },
];

// Category filter: "systems" / "aux" rollups + each specific category. The
// specific options are the systems Sedgwick actually orders, so a buyer can
// scope straight to "just furnaces" or "just A/C".
type CatFilter = "systems" | "aux" | "all" | EquipCategory;
const CAT_FILTERS: [CatFilter, string][] = [
  ["systems", "All whole systems"],
  ["furnace", CATEGORY_LABEL.furnace],
  ["ac", CATEGORY_LABEL.ac],
  ["coil", CATEGORY_LABEL.coil],
  ["heat-pump", CATEGORY_LABEL["heat-pump"]],
  ["mini-split", CATEGORY_LABEL["mini-split"]],
  ["magic-pak", CATEGORY_LABEL["magic-pak"]],
  ["rooftop", CATEGORY_LABEL.rooftop],
  ["garage-heater", CATEGORY_LABEL["garage-heater"]],
  ["air-handler", CATEGORY_LABEL["air-handler"]],
  ["water-heater", CATEGORY_LABEL["water-heater"]],
  ["aux", "All aux & IAQ"],
  ["all", "All equipment"],
];

type SeriesFilter = "all" | EquipSeries;
const SERIES_FILTERS: [SeriesFilter, string][] = [
  ["all", "All series"],
  ["signature", SERIES_LABEL.signature],
  ["elite", SERIES_LABEL.elite],
  ["merit", SERIES_LABEL.merit],
  ["armstrong", SERIES_LABEL.armstrong],
  ["other", SERIES_LABEL.other],
];

const LOOKBACKS: [number, string][] = [[3, "3 years"], [4, "4 years"], [5, "5 years"], [6, "6 years"]];

// Rollup grain. "model" = each distinct model (the default). "size" = fold to
// the refrigerant-agnostic family + tonnage ("EL16 · 2.0 ton") so a buyer sees
// how many of each SIZE to bring in across years — the view that drives a
// condenser order (Seth's EL15/EL16-by-tonnage ask). Sizes parse from A/C +
// heat-pump capacity codes; anything without one falls to a visible bucket.
type Rollup = "model" | "size";
const ROLLUPS: [Rollup, string][] = [["model", "Each model"], ["size", "By size (tonnage)"]];

// Does a row pass the category filter?
function matchesCat(r: EquipInstallRow, cat: CatFilter): boolean {
  if (cat === "all") return r.tier === "system" || r.tier === "auxiliary";
  if (cat === "systems") return SYSTEM_CATEGORIES.has(r.category);
  if (cat === "aux") return AUX_CATEGORIES.has(r.category);
  return r.category === cat;
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// The most recent COMPLETED occurrence of a season determines the latest column.
// For summer: before September, last complete summer is last year's.
function latestSeasonYear(months: number[]): number {
  const now = new Date();
  const m = now.getMonth() + 1;
  const lastMonth = months[months.length - 1];
  // Winter wraps the year-end; treat its "year" as the Dec start year. Simplify:
  // a season is complete once we're past its last month in the calendar.
  if (months.includes(12) && months.includes(1)) {
    // winter Dec–Feb: complete after Feb → belongs to the Dec year
    return m >= 3 ? now.getFullYear() - 1 : now.getFullYear() - 2;
  }
  return m > lastMonth ? now.getFullYear() : now.getFullYear() - 1;
}

function seasonYearOf(dateISO: string, months: number[]): number | null {
  const y = parseInt(dateISO.slice(0, 4), 10);
  const m = parseInt(dateISO.slice(5, 7), 10);
  if (!months.includes(m)) return null;
  // Winter Dec–Feb: Jan/Feb belong to the PRIOR Dec's season-year.
  if (months.includes(12) && months.includes(1)) {
    return m <= 2 ? y - 1 : y;
  }
  return y;
}

function StyledSelect({ value, onChange, options }: {
  value: string; onChange: (v: string) => void; options: [string, string][];
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 cursor-pointer appearance-none rounded-lg border border-hairline bg-white pl-3 pr-9 text-sm font-medium text-slate-700 transition-colors duration-150 ease-out-quart hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
      >
        {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    </div>
  );
}

interface PivotRow {
  model: string;
  mfg: string;
  byYear: Map<number, number>;
  total: number;
  projection: number;
  trend: "up" | "down" | "flat";
  declining: boolean; // sharp sustained drop → likely phased-out, don't over-order
  onHand: number | null; // current stock (null = no stock match found)
  order: number | null;  // projected demand − on hand, floored at 0
  cores: Set<string>;    // contributing stock-match keys (1 in model mode, many in size mode)
}

export function SeasonalDemandReport() {
  const navigate = useNavigate();
  const [season, setSeason] = useState<SeasonKey>("summer");
  const [cat, setCat] = useState<CatFilter>("systems");
  const [series, setSeries] = useState<SeriesFilter>("all");
  const [modelQuery, setModelQuery] = useState<string>("");
  const [lookback, setLookback] = useState<number>(4);
  const [rollup, setRollup] = useState<Rollup>("model");

  const seasonDef = SEASONS.find((s) => s.key === season)!;
  const latestYear = latestSeasonYear(seasonDef.months);
  const years = Array.from({ length: lookback }, (_, i) => latestYear - (lookback - 1) + i);
  const nextYear = latestYear + 1;

  // Pull installs from the start of the earliest year through today (one cached
  // query), then pivot client-side by season-year.
  const fromISO = `${years[0]}-01-01`;
  const { data, isLoading, isError } = useEquipmentInstalls(fromISO, todayISO());
  const { data: stock } = useStockOnHand();

  const { rows, totalsByYear, grandTotal, projTotal, orderTotal, onHandTotal, matchedCount } = useMemo(() => {
    const all = data?.rows ?? [];
    const q = modelQuery.trim().toUpperCase();
    const filtered = all.filter((r: EquipInstallRow) => {
      if (!matchesCat(r, cat)) return false;
      if (series !== "all" && deriveSeries(r.mfg, r.model) !== series) return false;
      if (q && !`${(r.mfg ?? "").toUpperCase()} ${(r.model ?? "").toUpperCase()}`.includes(q)) return false;
      return true;
    });
    // Pivot: model → year → count.
    const pivot = new Map<string, PivotRow>();
    const totalsByYear = new Map<number, number>();
    for (const r of filtered) {
      const sy = seasonYearOf(r.install_date, seasonDef.months);
      if (sy === null || !years.includes(sy)) continue;
      // Collapse the factory revision suffix (-09 / -05) so the same unit is one
      // row — what matters for "how many to order" (revision = a parts concern).
      const mfgU = (r.mfg ?? "").trim().toUpperCase();
      let label: string, key: string, mfgShown: string;
      if (rollup === "size") {
        // Fold to family + tonnage so EL16XC1 (R-410A) and EL16KC1 (R-454B)
        // land in ONE "EL16 · 2.0 ton" row across years. No capacity code (a
        // furnace, an odd model) → a visible "size n/a" bucket, never dropped.
        const tons = condenserTons(r.model);
        const fam = modelFamily(r.model) || mfgU || "(model)";
        label = tons === null ? `${fam} · size n/a` : `${fam} · ${tons.toFixed(1)} ton`;
        key = `${fam}|${tons === null ? "na" : tons}`;
        mfgShown = "";
      } else {
        // Collapse the factory revision suffix so the same unit is one row.
        label = baseModel(r.model) || "(no model)";
        key = `${mfgU}|${label}`;
        mfgShown = mfgU;
      }
      if (!pivot.has(key)) {
        pivot.set(key, { model: label, mfg: mfgShown, byYear: new Map(), total: 0, projection: 0, trend: "flat", declining: false, onHand: null, order: null, cores: new Set() });
      }
      const pr = pivot.get(key)!;
      pr.cores.add(coreModel(r.model));
      pr.byYear.set(sy, (pr.byYear.get(sy) ?? 0) + 1);
      pr.total += 1;
      totalsByYear.set(sy, (totalsByYear.get(sy) ?? 0) + 1);
    }
    // Projection: recency-weighted mean. Weights grow EXPONENTIALLY toward the
    // newest year (×2 per step), so the most recent season dominates. This is
    // deliberate for the R-454B transition: a legacy R-410A model going
    // 43→34→7→1 should project ~low (it's dying), and a new R-454B/KC model
    // ramping 0→8→20 should project toward its recent level, not be dragged
    // down by the zeros before it existed. Still a simple weighted average, not
    // a regression — explained in the footnote.
    const lastYear = years[years.length - 1];
    const prevYear = years[years.length - 2];
    let projTotal = 0;
    const rows = Array.from(pivot.values()).map((pr) => {
      let wsum = 0, w = 0;
      years.forEach((y, i) => {
        const weight = Math.pow(2, i); // older→newer = 1,2,4,8,...
        wsum += (pr.byYear.get(y) ?? 0) * weight;
        w += weight;
      });
      pr.projection = Math.round(w > 0 ? wsum / w : 0);
      projTotal += pr.projection;
      const last = pr.byYear.get(lastYear) ?? 0;
      const prev = pr.byYear.get(prevYear) ?? 0;
      pr.trend = last > prev ? "up" : last < prev ? "down" : "flat";
      // Declining = a model with real history that has nearly dried up in the
      // most recent season (≤25% of its peak, peak ≥3). The flag tells the
      // buyer "phased-out — don't restock to the old level" even though the
      // weighted projection may still show a small residual number.
      // "Phasing out" applies to a specific MODEL being discontinued (e.g. an
      // R-410A unit) — never to a SIZE, which lives on across refrigerant
      // generations. In the by-size fold, a family can look like it's drying up
      // (EL16XC1 → EL16KC1 + a genuine share dip) when it's really just
      // continuing at lower volume, so we don't flag/zero it there.
      const peak = Math.max(...years.map((y) => pr.byYear.get(y) ?? 0));
      pr.declining = rollup === "model" && peak >= 3 && last <= Math.ceil(peak * 0.25) && pr.total >= 5;
      // Order target: projected demand − current stock on hand, floored at 0.
      // onHand is null when no stock row matches (so we show "—", not "order
      // everything"); a phased-out model never recommends an order.
      // Stock on hand = sum of every contributing model's snapshot qty (one
      // model in "each model" mode, all sizes' models in "by size" mode). null
      // only when NONE matched — so we show "—", never a false "order all".
      let oh: number | null = null;
      for (const c of pr.cores) { const st = stock?.byCore.get(c); if (st) oh = (oh ?? 0) + st.qty; }
      pr.onHand = oh;
      pr.order = pr.onHand === null
        ? null
        : pr.declining ? 0 : Math.max(0, pr.projection - pr.onHand);
      return pr;
    });
    rows.sort((a, b) => b.total - a.total);
    const grandTotal = rows.reduce((s, r) => s + r.total, 0);
    const orderTotal = rows.reduce((s, r) => s + (r.order ?? 0), 0);
    const onHandTotal = rows.reduce((s, r) => s + (r.onHand ?? 0), 0);
    const matchedCount = rows.filter((r) => r.onHand !== null).length;
    return { rows, totalsByYear, grandTotal, projTotal, orderTotal, onHandTotal, matchedCount };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, stock, cat, series, modelQuery, season, lookback, rollup]);

  function exportCsv() {
    const lines: string[] = [];
    lines.push(`Sedgwick Heating — Seasonal demand (${seasonDef.label})`);
    lines.push(`Category,${CAT_FILTERS.find(([k]) => k === cat)?.[1]}`);
    lines.push(`Series,${SERIES_FILTERS.find(([k]) => k === series)?.[1]}`);
    lines.push(`Roll up,${ROLLUPS.find(([k]) => k === rollup)?.[1]}`);
    if (modelQuery.trim()) lines.push(`Model search,"${modelQuery.trim().replace(/"/g, '""')}"`);
    lines.push(`Order = projected demand − stock on hand (floored 0; phasing-out = 0). Stock source,${stock?.source ?? "n/a"}${stock?.asOf ? ` as of ${stock.asOf}` : ""}`);
    lines.push("");
    lines.push(["Make", rollup === "size" ? "Size" : "Model", ...years.map(String), `Projected ${nextYear}`, "On hand", "Order"].join(","));
    for (const r of rows) {
      const cells = years.map((y) => String(r.byYear.get(y) ?? 0));
      const safeMfg = /[",\n]/.test(r.mfg) ? `"${r.mfg.replace(/"/g, '""')}"` : r.mfg;
      const safeModel = /[",\n]/.test(r.model) ? `"${r.model.replace(/"/g, '""')}"` : r.model;
      lines.push([safeMfg, safeModel, ...cells, String(r.projection), r.onHand === null ? "" : String(r.onHand), r.order === null ? "" : String(r.order)].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `seasonal-demand_${season}_${cat}_${rollup === "size" ? "by-size" : "by-model"}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const TOP = 25;
  const shown = rows.slice(0, TOP);

  return (
    <div>
      {/* Ask Pegasus entry — for a one-off question this table can't shape, jump
          to the conversational analyst (which can also answer install counts). */}
      <button
        type="button"
        onClick={() => navigate("/reports?report=ask")}
        className="group mb-4 flex w-full items-center gap-2.5 rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50/70 via-white to-white px-4 py-2.5 text-left transition-colors hover:border-violet-300"
      >
        <Sparkles className="h-4 w-4 shrink-0 text-violet-500" />
        <span className="text-sm text-slate-600"><span className="font-semibold text-violet-700">Have a specific question?</span> Ask Pegasus.</span>
        <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-violet-400 transition-transform group-hover:translate-x-0.5" />
      </button>

      {/* Controls */}
      <Card className="mb-5 p-4">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <Field label="Season"><StyledSelect value={season} onChange={(v) => setSeason(v as SeasonKey)} options={SEASONS.map((s) => [s.key, s.label])} /></Field>
          <Field label="Category"><StyledSelect value={cat} onChange={(v) => setCat(v as CatFilter)} options={CAT_FILTERS} /></Field>
          <Field label="Series"><StyledSelect value={series} onChange={(v) => setSeries(v as SeriesFilter)} options={SERIES_FILTERS} /></Field>
          <Field label="Model search">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={modelQuery}
                onChange={(e) => setModelQuery(e.target.value)}
                placeholder="e.g. EL297, KC, SL280"
                className="h-9 w-44 rounded-lg border border-hairline bg-white pl-8 pr-7 text-sm text-slate-700 transition-colors duration-150 ease-out-quart hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
              />
              {modelQuery && (
                <button type="button" onClick={() => setModelQuery("")} aria-label="Clear model search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-700">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </Field>
          <Field label="Look back"><StyledSelect value={String(lookback)} onChange={(v) => setLookback(Number(v))} options={LOOKBACKS.map(([n, l]) => [String(n), l])} /></Field>
          <Field label="Roll up"><StyledSelect value={rollup} onChange={(v) => setRollup(v as Rollup)} options={ROLLUPS} /></Field>
          <div className="ml-auto">
            <Button variant="secondary" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
              <Download className="h-4 w-4" /> Export CSV
            </Button>
          </div>
        </div>
      </Card>

      {/* Order-target status — stock snapshot is wired; be honest about source + match rate */}
      <div className="mb-4 flex items-center gap-1.5 rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-xs text-slate-600">
        <span>
          Stock on hand{stock?.asOf ? ` as of ${stock.asOf}` : ""} · {matchedCount} of {rows.length} shown models matched a stock row
        </span>
        <InfoTip label="How Order is figured">
          Order is projected demand minus stock on hand, never below 0, and 0 for a model that's phasing out. Stock is a{" "}
          {stock?.source === "manual_snapshot" ? "manual snapshot" : "snapshot"} of INSTOCK UNITS.xlsx; re-import it to refresh.
          Model matching is best-effort; a dash means no stock row matched.
        </InfoTip>
      </div>

      {isError ? (
        <Card className="p-10 text-center"><p className="text-sm text-slate-600">Couldn't load install history. Check the connection and try again.</p></Card>
      ) : isLoading ? (
        <Card className="p-10 text-center"><p className="text-sm text-slate-500">Loading install history…</p></Card>
      ) : rows.length === 0 ? (
        <Card className="p-10 text-center">
          <PackageSearch className="mx-auto h-10 w-10 text-slate-300" />
          <h3 className="mt-3 text-[15px] font-bold tracking-tight text-slate-900">No installs in these seasons</h3>
          <p className="mt-1 text-xs text-slate-500">Try a different season, a wider lookback, or another equipment type.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hairline bg-slate-50/60 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5">{rollup === "size" ? "Size" : "Model"}</th>
                {years.map((y) => <th key={y} className="px-3 py-2.5 text-right tabular-nums">{y}</th>)}
                <th className="px-3 py-2.5 text-center">Trend</th>
                <th className="px-4 py-2.5 text-right">
                  <span className="inline-flex items-center gap-1.5">
                    Projected {nextYear}
                    <InfoTip className="normal-case" align="end" label="How the projection works">A recency-weighted average of the seasons shown, with the latest season counting most. A simple baseline, not a statistical forecast. Counts use equipment with a known install date.</InfoTip>
                  </span>
                </th>
                <th className="px-3 py-2.5 text-right">On hand</th>
                <th className="px-4 py-2.5 text-right">Order</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map((r) => (
                <tr key={`${r.mfg}|${r.model}`} className="transition-colors hover:bg-brand-50/40">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800">{r.model}</span>
                      {r.declining && (
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500" title="Installs have dropped sharply — likely a phased-out model (e.g. R-410A). Don't restock to the old level.">
                          Phasing out
                        </span>
                      )}
                    </div>
                    {r.mfg && <div className="text-[11px] text-slate-400">{r.mfg}</div>}
                  </td>
                  {years.map((y) => {
                    const v = r.byYear.get(y) ?? 0;
                    return <td key={y} className={`px-3 py-2.5 text-right tabular-nums ${v === 0 ? "text-slate-300" : "text-slate-700"}`}>{v}</td>;
                  })}
                  <td className="px-3 py-2.5 text-center">
                    {r.trend === "up" && <TrendingUp className="mx-auto h-4 w-4 text-emerald-500" />}
                    {r.trend === "down" && <TrendingDown className="mx-auto h-4 w-4 text-amber-500" />}
                    {r.trend === "flat" && <Minus className="mx-auto h-4 w-4 text-slate-300" />}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <span className="text-sm font-medium tabular-nums text-slate-600">{r.projection}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">
                    {r.onHand === null ? <span className="text-slate-300">—</span> : r.onHand}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {r.order === null ? (
                      <span className="text-slate-300" title="No matching stock row — can't compute an order target">—</span>
                    ) : r.order === 0 ? (
                      <span className="text-sm font-medium tabular-nums text-slate-400" title={r.declining ? "Phasing out — don't restock" : "Enough on hand"}>0</span>
                    ) : (
                      <span className="inline-flex items-center rounded-md bg-brand-50 px-2 py-0.5 text-sm font-semibold tabular-nums text-brand-700">{r.order}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-hairline bg-slate-50/60 font-semibold">
                <td className="px-4 py-2.5 text-slate-900">Total{rows.length > TOP ? ` (top ${TOP} of ${rows.length})` : ""}</td>
                {years.map((y) => <td key={y} className="px-3 py-2.5 text-right tabular-nums text-slate-900">{totalsByYear.get(y) ?? 0}</td>)}
                <td className="px-3 py-2.5" />
                <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{projTotal}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{onHandTotal}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-brand-700">{orderTotal}</td>
              </tr>
            </tfoot>
          </table>
        </Card>
      )}

      <p className="mt-4 text-xs text-slate-400">Grand total of {grandTotal.toLocaleString()} installs across the window.</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      {children}
    </label>
  );
}
